import {useEffect, useMemo, useState} from "react";
import MrclText from "../../marcel/MrclText";
import { formatGap, formatKvalueCASector, formatPercentage } from "../../../utils/numberUtils";
import useBreakPoint from "../../../utils/useBreakPoint";
import { useTranslation } from "react-i18next";
import { BodyRow, Card, ChevronContainer, ChevronSpacer, FootText, HierarchyCell, HierarchyContent, Legend, MetricCell, MetricText, Table, TableWrapper, Toolbar, ToolbarActions } from "../components/commonComponents";
import EmptyDataCard from "../components/EmptyDataCard";
import TableHeader from "../components/TableHeader";
import { Breakpoint, PerformanceApiGroup, PerformanceRadiusDetailProps, Row } from "../Performance.model";
import { ChevronIcon } from "../components/ChevronIcon";
import LoadingCard from "../components/LoadingCard";
import { COLORS } from "../../../utils/colors";
import { formatTimeLabel } from "../../../utils/dateUtils";
import { usePerformance } from "../context/PerformanceContext";

type MetricKey =
  | "caN"
  | "caN1"
  | "progCA"
  | "progPrev"
  | "clientsN"
  | "clientsN1"
  | "progClients"
  | "quotePart"
  | "objective";
  

const LEVELS: {color: string, label: string}[] = [
    {color: "#173EB4", label: "type"},
    {color: COLORS.green, label: "secteur"},
    {color: "#8A5F00", label: "rayon"},
    {color: "#0970E6", label: "sous-rayon"}];

function formatValue(value: number | null): string {
  return null == value ? "-" :  formatKvalueCASector(value, null, true);
}

function formatPercent(value: number | null): string {
  if (value === null) return "";
  const formatted = formatPercentage(value, "%", true);
  return value >= 0 ? `+${formatted}` : formatted;
}

function parseFormattedCurrencyToKValue(value: string): number {
  if (!value || value === "-") {
    return 0;
  }

  const normalized = value
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const amountMatch = normalized.match(/[-+]?\d+[\d\s]*(?:,\d+)?/);
  if (!amountMatch) {
    return 0;
  }

  const numericPart = amountMatch[0]
    .replace(/\s/g, "")
    .replace(",", ".");

  const amount = Number(numericPart);
  if (Number.isNaN(amount)) {
    return 0;
  }

  const lower = normalized.toLowerCase();
  if (lower.includes("md€")) {
    return amount * 1000000;
  }

  if (lower.includes("m€")) {
    return amount * 1000;
  }

  if (lower.includes("k€")) {
    return amount;
  }

  if (lower.includes("€")) {
    return amount / 1000;
  }

  return amount;
}

const ALIMENTAIRE_SECTORS = new Set(["SDV", "PGC", "PFT"]);

function normalizeSectorCode(sector: string | null | undefined): string | null {
  const normalizedSector = sector?.trim();

  return normalizedSector ? normalizedSector.toLowerCase() : null;
}

function buildTypeSummaryRow(
  id: string,
  label: string,
  children: Row[],
  progPrev: string = "-",
  objective: string = "-",
): Row {
  const numericChildren = children.filter((child) => child.caN !== "-");

  if (!numericChildren || !numericChildren.length) {
    return {
      id,
      label,
      badge: "Type",
      level: "type",
      caN: "-",
      caN1: "-",
      progCA: "-",
      progPrev,
      clientsN: "-",
      clientsN1: "-",
      progClients: "-",
      quotePart: "-",
      objective: "-",
      children,
    };
  }
  const totalCaN = numericChildren.reduce((sum, child) => sum + parseFormattedCurrencyToKValue(child.caN), 0);
  const totalCaN1 = numericChildren.reduce((sum, child) => sum + parseFormattedCurrencyToKValue(child.caN1), 0);
  const totalProgCA = totalCaN1 === 0 ? null : ((totalCaN - totalCaN1) / totalCaN1) * 100;
  const totalObjective = numericChildren.reduce((sum, child) => sum + parseFormattedCurrencyToKValue(child.objective), 0);

  return {
    id,
    label,
    badge: "Type",
    level: "type",
    caN: formatValue(
      totalCaN,
    ),
    caN1: formatValue(
      totalCaN1,
    ),
    progCA: formatPercent(
      totalProgCA,
    ),
    progPrev,
    clientsN: "-",
    clientsN1: "-",
    progClients: "-",
    quotePart: "-",
    objective: formatValue(totalObjective),
    children,
  };
}

function buildRowsFromApi(groups: PerformanceApiGroup[]): Row[] {
  if (!groups || !groups.length) {
    return [];
  }

  const sectorTotals = groups[0]?.item ?? [];
  const departments = groups.slice(1).flatMap((group) => group.item);

  const sectorRows: Row[] = sectorTotals.flatMap((sectorItem): Row[] => {
    const sectorCode = normalizeSectorCode(sectorItem.sector);

    if (!sectorCode) {
      return [];
    }

    const sectorDepartments = departments.filter((department) => department.sector === sectorItem.sector);
    const totalObjective= sectorDepartments.reduce((sum, dept) => sum + (dept.objective ?? 0), 0);


    return [{
      id: `secteur-${sectorCode}`,
      label: `Secteur ${sectorItem.sector}`,
      badge: "Secteur",
      level: "secteur",
      caN: formatValue(sectorItem.ca),
      caN1: formatValue(sectorItem.lastCa),
      progCA: formatPercent(sectorItem.rh),
      progPrev: sectorItem.ca != null
              ? formatGap(sectorItem.ca - totalObjective)
              : "-",
      clientsN: "-",
      clientsN1: "-",
      progClients: "-",
      quotePart: "-",
      objective: formatValue(totalObjective),
      children: sectorDepartments.map(
        (department): Row => ({
          id: `rayon-${normalizeSectorCode(department.sector) ?? "unknown"}-${department.hypDepartmentKey}`,
          label: `${department.hypDepartmentKey} - ${department.hypDepartmentDesc}`,
          badge: "Rayon",
          level: "rayon",
          caN: formatValue(department.ca),
          caN1: formatValue(department.lastCa),
          progCA: formatPercent(department.rh),
          progPrev: formatGap((department.ca ?? 0) - (department.objective ?? 0)),
          clientsN: "-",
          clientsN1: "-",
          progClients: "-",
          quotePart: "-",
          objective: formatValue(department.objective ?? 0)
        }),
      ),
    }];
  });

  const alimentaireSectors = sectorRows.filter((row) => {
    const sectorCode = row.id.replace("secteur-", "").toUpperCase();
    return ALIMENTAIRE_SECTORS.has(sectorCode);
  });

  const nonAlimentaireSectors = sectorRows.filter((row) => {
    const sectorCode = row.id.replace("secteur-", "").toUpperCase();
    return !ALIMENTAIRE_SECTORS.has(sectorCode);
  });

  const alimentaireSectorItems = sectorTotals.filter((sectorItem) => {
    const sectorCode = normalizeSectorCode(sectorItem.sector)?.toUpperCase();
    return sectorCode && ALIMENTAIRE_SECTORS.has(sectorCode);
  });

  const nonAlimentaireSectorItems = sectorTotals.filter((sectorItem) => {
    const sectorCode = normalizeSectorCode(sectorItem.sector)?.toUpperCase();
    return sectorCode && !ALIMENTAIRE_SECTORS.has(sectorCode);
  });

  const alimentaireDeps = departments.filter((dept) => {
    const sectorCode = normalizeSectorCode(dept.sector)?.toUpperCase();
    return sectorCode && ALIMENTAIRE_SECTORS.has(sectorCode);
  });

  const nonAlimentaireDeps = departments.filter((dept) => {
    const sectorCode = normalizeSectorCode(dept.sector)?.toUpperCase();
    return sectorCode && !ALIMENTAIRE_SECTORS.has(sectorCode);
  });

  const totalAlimCa = alimentaireSectorItems.reduce((sum, item) => sum + (item.ca ?? 0), 0);
  const totalAlimProj = alimentaireDeps.reduce((sum, dept) => sum + (dept.objective ?? 0), 0);

  const totalNonAlimCa = nonAlimentaireSectorItems.reduce((sum, item) => sum + (item.ca ?? 0), 0);
  const totalNonAlimProj = nonAlimentaireDeps.reduce((sum, dept) => sum + (dept.objective ?? 0), 0);

  const hasAlimCa = alimentaireSectorItems.some((item) => item.ca != null);
  const alimProgPrev = hasAlimCa ? formatGap(totalAlimCa - totalAlimProj) : "-";

  const hasNonAlimCa = nonAlimentaireSectorItems.some((item) => item.ca != null);
  const nonAlimProgPrev = hasNonAlimCa ? formatGap(totalNonAlimCa - totalNonAlimProj) : "-";

  return [buildTypeSummaryRow("type-alimentaire", "Alimentaire", alimentaireSectors, alimProgPrev),
    buildTypeSummaryRow("type-non-alimentaire", "Non Alimentaire", nonAlimentaireSectors, nonAlimProgPrev),
  ];
}

const levelIndent: Record<string, number> = {
  type: 0,
  secteur: 20,
  rayon: 40,
  "sous-rayon": 60,
};


function PercentBadge({ value, isBold }: { value: string; isBold?: boolean }) {
  const isNeg = value.startsWith("-") && value !== "-";
  const isPos = value.startsWith("+");
  return (
    <MetricText
      style={{
        color: isNeg ? COLORS.red : isPos ? COLORS.green : "#374151",
        fontWeight: isBold ? 700 : 400,
      }}
    >
      {value}
    </MetricText>
  );
}

interface TableRowProps {
  row: Row;
  expandedIds: Set<string>;
  toggleExpand: (id: string) => void;
  visibleMetricKeys: MetricKey[];
}

function TableRow({ row, expandedIds, toggleExpand, visibleMetricKeys }: TableRowProps) {
  const isExpanded = expandedIds.has(row.id);
  const hasChildren = row.children && row.children.length > 0;
  const indent = levelIndent[row.level];
  const isType = row.level === "type";
  const isSecteur = row.level === "secteur";
  const isRayon = row.level === "rayon";
  const variant: "type" | "secteur" | "default" | "rayon" | "sous-rayon" = isType ? "type" : isSecteur ? "secteur" : isRayon ? "rayon" : "default";

  return (
    <>
      <BodyRow 
        $variant={variant} 
        $clickable={Boolean(hasChildren)} 
        onClick={() => hasChildren && toggleExpand(row.id)} 
        $index={undefined}
        style={{
          borderLeft: `4px solid ${LEVELS.find(({label}) => label === row.level)?.color ?? ''}`,
        }}
      >


        <HierarchyCell>
          <HierarchyContent style={{ paddingLeft: indent }}>
            {hasChildren ? (
              <ChevronContainer style={{ color: LEVELS.find(({label}) => label === row.level)?.color ?? '', }}>
                <ChevronIcon open={isExpanded} />
              </ChevronContainer>
            ) : (
              <ChevronSpacer />
            )}
            <MrclText
              bold={isType || isSecteur}
              style={{
                margin: 0,
                fontSize: 16,
                color: "#1e293b",
              }}
            >
              {row.label}
            </MrclText>
          </HierarchyContent>
        </HierarchyCell>

        {visibleMetricKeys.map((metricKey) => (
          <MetricCell key={metricKey}><PercentBadge value={row[metricKey]} isBold={isType || isSecteur} /></MetricCell>
        ))}
      </BodyRow>

      {isExpanded &&
        hasChildren &&
        row.children!.map((child) => (
          <TableRow
            key={child.id}
            row={child}
            expandedIds={expandedIds}
            toggleExpand={toggleExpand}
            visibleMetricKeys={visibleMetricKeys}
          />
        ))}
    </>
  );
}

function getAllIds(rows: Row[]): string[] {
  return rows.flatMap((r) => [r.id, ...(r.children ? getAllIds(r.children) : [])]);
}

function countVisibleRows(rows: Row[], expandedIds: Set<string>): number {
  return rows.reduce((count, row) => {
    const visibleChildren = expandedIds.has(row.id) && row.children
      ? countVisibleRows(row.children, expandedIds)
      : 0;

    return count + 1 + visibleChildren;
  }, 0);
}

export default function PerformanceRadiusDetail({ listValue, range, isLoading }: PerformanceRadiusDetailProps) {
  const { isCALive } = usePerformance();
  const data: Row[] = buildRowsFromApi(listValue);
  const breakPoint = useBreakPoint() as Breakpoint;
  const allIds = useMemo(() => getAllIds(data), [data]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const importedDateTime = listValue?.flatMap((group) => group.item).find((item) => item.importedDateTime)?.importedDateTime;

  let rangeCol = range === "CUSTOM" ? "Manuel" :range;
  const METRIC_COLUMNS: { key: MetricKey; header: string; isVisible: (breakpoint: Breakpoint) => boolean }[] = [
    {
      key: "caN1",
      header: `CA ${rangeCol}`,
      isVisible: () => true,
    },
      {
      key: "caN",
      header: "CA N",
      isVisible: () => true,
    },
    {
      key: "progCA",
      header: isCALive ? "PROG CA" : `VAR ${rangeCol}`,
      isVisible: () => true,
    },
    {
      key: "objective",
      header: isCALive ? "BUDGET" : "OBJECTIF",
      isVisible: () => true,
    },
    {
      key: "progPrev",
      header: isCALive ? "R-B" : "ECART",
      isVisible: () => true,
    },
    {
      key: "clientsN",
      header: "Clients N",
      isVisible: () => false,
    },
    {
      key: "clientsN1",
      header: `Clients ${rangeCol}`,
      isVisible: () => false,
    },
    {
      key: "progClients",
      header: isCALive ? "PROG CLIENTS" : "VAR CLIENTS",
      isVisible: () => false,
    },
    {
      key: "quotePart",
      header: "QUOTE PART",
      isVisible: () => false,
    }
  ];
  
  const visibleColumns = METRIC_COLUMNS.filter((column) => column.isVisible(breakPoint));
  const visibleMetricKeys = visibleColumns.map((column) => column.key);
  const visibleRowCount = countVisibleRows(data, expandedIds);

  useEffect(() => {
    if (!initialized && allIds.length > 0) {
      setExpandedIds(new Set(allIds));
      setInitialized(true);
    }
  }, [allIds, initialized]);
  const {t} = useTranslation();


  const onClick = () => {
    setExpandedIds((prev) => {
      return !!prev?.size ? new Set() : new Set(allIds);
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return <LoadingCard />;
  }

  if (!data || !data.length) {
    return <EmptyDataCard/>
  }

  return (
    <Card>
      <TableWrapper>
        <Table>
          <TableHeader visibleColumns={visibleColumns} title={t('commercial.hierarchie')}>

            <mrcl-icon-button
              variant="transparent"
              tone="neutral"
              style={{ background: 'none', color: '#005CA9', border: 'none', padding: 0, margin: 0 }}
              icon={!!expandedIds?.size ? "chevron-double-up" : "chevron-double-down"}
              onClick={onClick}
            />

          </TableHeader>
          <tbody>
            {data.map((row) => (
              <TableRow
                key={row.id}
                row={row}
                expandedIds={expandedIds}
                toggleExpand={toggleExpand}
                visibleMetricKeys={visibleMetricKeys}
              />
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      {!!importedDateTime &&
        <Toolbar>
          <Legend>
            <FootText>
              {`${t('commercial.data-updated')} ${formatTimeLabel(importedDateTime)}`}
            </FootText>
          </Legend>

          <ToolbarActions>
            <FootText>
              {`${t('commercial.total-lignes')} ${visibleRowCount}`}
            </FootText>
          </ToolbarActions>
        </Toolbar>}
    </Card>
  );
}
